import React, { useEffect, useMemo, useState } from 'react';
import { objectionCountLabel } from '../services/risResearchStream';
import { DETAIL_PENDING_MESSAGE } from '../services/researchDetailStream';
import type { DetailStreamStatus } from '../services/researchDetailStream';
import type { ResearchSuggestion, RevealSection, ReviewAnnotation } from '../services/risResearchStream';

/**
 * Die zweite Schicht einer Klartext-Antwort: die Vorschlags-Chips und der
 * eingeklappte Bereich "Ausführliche Analyse". Der Klartext selbst steht
 * darüber und bleibt unverändert. Der ausführliche Text ist derselbe A-G-Text
 * wie im Kanzlei-Profil; er wird abschnittsweise durch denselben Weg gerendert
 * wie die Antwort darüber, damit Fundstellen den Viewer genauso öffnen.
 */

/** Buchstabe eines Abschnitts der ausführlichen Antwort. */
export type DetailedSectionLetter = 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G';

export interface DetailedSection {
  /** Der Buchstabe der Überschrift, mit der dieser Teil beginnt. */
  letter: DetailedSectionLetter | null;
  text: string;
}

/**
 * Überschriftenzeile eines A-G-Abschnitts. Dieselbe Regel entscheidet auf der
 * Serverseite, für welchen Abschnitt es überhaupt einen Aufklapp-Chip gibt
 * (hasSectionHeading in
 * server/src/services/RISearch_research/klartextComposition.ts): Buchstabe,
 * Punkt und Leerraum, davor höchstens drei Rauten und ein öffnender Fettdruck;
 * was hinter dem Titel noch steht, bleibt gleichgültig. Würde hier eine engere
 * Regel gelten, zeigte ein Chip auf einen Anker, den es nicht gibt.
 */
const SECTION_HEADING = /^[ \t]*(?:#{1,3}[ \t]*)?(?:\*\*)?([A-G])\.[ \t]/;

function sectionLetterOfLine(line: string): DetailedSectionLetter | null {
  const heading = SECTION_HEADING.exec(line);
  return heading ? heading[1] as DetailedSectionLetter : null;
}

/**
 * Der ausführliche Text, zerlegt an den Überschriften der obersten Ebene. Eine
 * Überschrift beendet in der Darstellung jeden offenen Block, deshalb ist der
 * Schnitt an dieser Stelle gefahrlos: kein Zitatblock und keine Aufzählung
 * wird zerschnitten.
 */
export function splitDetailedSections(content: string): DetailedSection[] {
  const lines = content.split('\n');
  const sections: DetailedSection[] = [];
  let letter: DetailedSectionLetter | null = null;
  let buffer: string[] = [];

  const flush = () => {
    const text = buffer.join('\n');
    if (text.trim()) sections.push({ letter, text });
    buffer = [];
  };

  for (const line of lines) {
    const found = sectionLetterOfLine(line);
    if (found) {
      flush();
      letter = found;
    }
    buffer.push(line);
  }
  flush();
  return sections;
}

/** Ankerkennung eines Abschnitts, stabil je Nachricht. */
export function sectionAnchorId(messageKey: string, letter: string): string {
  return `analyse-${messageKey.replace(/[^A-Za-z0-9_-]/g, '-')}-${letter.toLowerCase()}`;
}

/** Kennung des Bereichs selbst; ein Chip ohne Abschnitt öffnet ihn oben. */
export function panelAnchorId(messageKey: string): string {
  return `analyse-${messageKey.replace(/[^A-Za-z0-9_-]/g, '-')}-panel`;
}

/** Abzeichen der anwaltlichen Prüfung am Kopf der ausführlichen Schicht. */
export function reviewBadgeLabel(review?: ReviewAnnotation): string {
  if (review?.status === 'certified') return 'Geprüft';
  if (review?.status === 'objections') return `Geprüft, ${objectionCountLabel(review.objections.length)}`;
  return 'Prüfung nicht verfügbar';
}

function reviewBadgeClass(review?: ReviewAnnotation): string {
  if (review?.status === 'certified') return 'border-emerald-300 bg-emerald-50 text-emerald-800';
  if (review?.status === 'objections') return 'border-amber-300 bg-amber-50 text-amber-900';
  return 'border-warm-200 bg-warm-100 text-muted';
}

const CHIP_CLASS = 'inline-flex items-center border border-warm-200 bg-warm-100 px-3 py-1.5 text-xs '
  + 'font-medium text-primary transition-colors hover:border-accent hover:bg-warm-200 '
  + 'dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:border-accent';

const CHIP_ROW_LABEL_CLASS = 'text-[11px] font-semibold uppercase tracking-[0.12em] text-muted dark:text-gray-400';

const Chevron: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    className={`h-4 w-4 shrink-0 transition-transform ${open ? 'rotate-90' : ''}`}
    viewBox="0 0 16 16"
    aria-hidden="true"
  >
    <path d="M6 3l5 5-5 5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * Die beiden Chip-Zeilen. Ohne eigenen Zustand, damit die Zuordnung von Chip
 * und Wirkung ohne Browser geprüft werden kann.
 */
export const SuggestionChips: React.FC<{
  suggestions: readonly ResearchSuggestion[];
  /** Ohne ausführliche Schicht gibt es nichts zu vertiefen; die Zeile entfällt. */
  revealAvailable?: boolean;
  onReveal: (section: RevealSection | null) => void;
  onAsk: (text: string) => void;
}> = ({ suggestions, revealAvailable = true, onReveal, onAsk }) => {
  const reveals = revealAvailable
    ? suggestions.filter((item): item is Extract<ResearchSuggestion, { kind: 'reveal' }> => item.kind === 'reveal')
    : [];
  const questions = suggestions.filter((item): item is Extract<ResearchSuggestion, { kind: 'question' }> => item.kind === 'question');
  if (!revealAvailable && questions.length === 0) return null;

  return (
    <div className="mt-3 space-y-2">
      {revealAvailable && (
      <div className="flex flex-wrap items-center gap-2">
        <span className={CHIP_ROW_LABEL_CLASS}>Vertiefen</span>
        <button type="button" className={CHIP_CLASS} onClick={() => onReveal(null)}>
          Ausführliche Analyse
        </button>
        {reveals.map((item, index) => (
          <button
            key={`reveal-${item.section}-${index}`}
            type="button"
            className={CHIP_CLASS}
            onClick={() => onReveal(item.section)}
          >
            {item.label}
          </button>
        ))}
      </div>
      )}
      {questions.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className={CHIP_ROW_LABEL_CLASS}>Weiterfragen</span>
          {questions.map((item, index) => (
            <button
              key={`question-${index}`}
              type="button"
              className={CHIP_CLASS}
              onClick={() => onAsk(item.text)}
            >
              {item.text}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Ob ein Aufklappen den Abruf anstoßen soll. Liegt der Text schon vor, gibt es
 * nichts zu holen; läuft ein Abruf, wäre ein zweiter überflüssig; ist einer
 * gescheitert, bleibt es beim Grund, bis der Leser den eigenen Knopf drückt.
 */
export function shouldRequestDetail(input: { hasText: boolean; status: DetailStreamStatus }): boolean {
  return !input.hasText && input.status !== 'loading' && input.status !== 'error';
}

/**
 * Der Kopf des aufgeklappten Bereichs, solange die Schicht noch entsteht oder
 * ihr Abruf gescheitert ist. Ohne eigenen Zustand, damit Meldung und Knopf ohne
 * Browser geprüft werden können.
 */
export const DetailPanelStatus: React.FC<{
  status: DetailStreamStatus;
  statusMessage?: string;
  errorMessage?: string;
  onRetry?: () => void;
}> = ({ status, statusMessage, errorMessage, onRetry }) => {
  if (status === 'loading') {
    return (
      <p className="mb-3 text-xs text-muted dark:text-gray-400">
        {statusMessage || DETAIL_PENDING_MESSAGE}
      </p>
    );
  }
  if (status !== 'error') return null;
  return (
    <div className="mb-3 border-l-2 border-amber-400 pl-3">
      <p className="text-sm text-primary dark:text-gray-200">
        {errorMessage || 'Die ausführliche Antwort konnte nicht abgerufen werden.'}
      </p>
      <button type="button" className={`mt-2 ${CHIP_CLASS}`} onClick={() => onRetry?.()}>
        Erneut versuchen
      </button>
    </div>
  );
};

export const AnswerLayers: React.FC<{
  /** Stabile Kennung der Nachricht; sie trägt die Anker der Abschnitte. */
  messageKey: string;
  detailedAnswer?: string;
  /**
   * Die ausführliche Schicht steht auf Abruf bereit. Mit diesem Vermerk zeigt
   * der Bereich seinen Kopf, obwohl noch kein Text da ist; das Aufklappen holt
   * ihn. Ohne Vermerk und ohne Text gibt es keinen Bereich -- so bleiben
   * Folgefragen aus dem Bestand ohne einen Knopf, der ins Leere führte.
   */
  detailAvailable?: boolean;
  /** Der Stand des Abrufs, solange er läuft oder gescheitert ist. */
  detailStatus?: DetailStreamStatus;
  detailStatusMessage?: string;
  detailErrorMessage?: string;
  /** Erstes Aufklappen: der Abruf beginnt. */
  onExpandDetail?: () => void;
  /** Nach einem gescheiterten Abruf: noch einmal. */
  onRetryDetail?: () => void;
  suggestions?: readonly ResearchSuggestion[];
  reviewAnnotation?: ReviewAnnotation;
  /** Solange der Lauf noch streamt, steht der ausführliche Text noch aus. */
  streaming?: boolean;
  /**
   * Die Einwände gegen den ausführlichen Text. Sie stehen im aufgeklappten
   * Bereich unter dem Abzeichen des Kopfes -- dort, wo die beanstandete Stelle
   * selbst steht, und nicht unter dem Klartext, der sie nicht führt.
   */
  objections?: React.ReactNode;
  /** Derselbe Darstellungsweg wie der Klartext darüber. */
  renderDetailedSection: (content: string) => React.ReactNode;
  onAskSuggestion: (text: string) => void;
}> = ({
  messageKey,
  detailedAnswer,
  detailAvailable = false,
  detailStatus = 'idle',
  detailStatusMessage,
  detailErrorMessage,
  onExpandDetail,
  onRetryDetail,
  suggestions,
  reviewAnnotation,
  streaming = false,
  objections,
  renderDetailedSection,
  onAskSuggestion,
}) => {
  const [open, setOpen] = useState(false);
  // Entsteht die Schicht gerade (etwa nach einem Reload wieder aufgenommen), steht der Bereich offen und zeigt den Lauf.
  useEffect(() => { if (detailStatus === 'loading') setOpen(true); }, [detailStatus]);
  const sections = useMemo(() => (detailedAnswer ? splitDetailedSections(detailedAnswer) : []), [detailedAnswer]);
  const panelId = panelAnchorId(messageKey);
  const badgeClass = `inline-flex border px-2 py-0.5 text-[11px] font-semibold ${reviewBadgeClass(reviewAnnotation)}`;
  const offersDetail = Boolean(detailedAnswer) || detailAvailable;

  // Ohne ausführliche Schicht und ohne den Vermerk, dass eine bereitsteht, gibt
  // es keinen Bereich und keine Aufklapp-Chips. Das Abzeichen der Prüfung
  // bleibt: es gehört zur Antwort, nicht zur Schicht.
  if (!offersDetail) {
    return (
      <div className="mt-4 border-t border-warm-200 pt-3 dark:border-gray-700">
        <SuggestionChips
          suggestions={suggestions || []}
          revealAvailable={false}
          onReveal={() => undefined}
          onAsk={onAskSuggestion}
        />
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className={badgeClass}>{reviewBadgeLabel(reviewAnnotation)}</span>
          {streaming && (
            <span className="text-xs text-muted dark:text-gray-400">Ausführliche Analyse wird vorbereitet …</span>
          )}
        </div>
      </div>
    );
  }

  /**
   * Das Aufklappen holt die Schicht, falls sie noch fehlt. Ein laufender Abruf
   * wird nicht ein zweites Mal angestoßen; ein gescheiterter bleibt stehen, bis
   * der Leser den eigenen Knopf drückt.
   */
  const requestDetail = () => {
    if (shouldRequestDetail({ hasText: Boolean(detailedAnswer), status: detailStatus })) onExpandDetail?.();
  };

  const revealSection = (section: RevealSection | null) => {
    setOpen(true);
    requestDetail();
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    const targetId = section ? sectionAnchorId(messageKey, section) : panelId;
    window.requestAnimationFrame(() => {
      document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const togglePanel = () => {
    setOpen((previous) => {
      if (!previous) requestDetail();
      return !previous;
    });
  };

  return (
    <div className="mt-4 border-t border-warm-200 pt-3 dark:border-gray-700">
      <SuggestionChips
        suggestions={suggestions || []}
        // Ein Aufklapp-Chip zeigt auf einen Abschnitt; ohne Text gibt es keinen
        // Anker, auf den er zeigen könnte. Die Chips kommen mit dem Abschluss
        // des Abrufs, nicht mit der Klartext-Antwort.
        revealAvailable={Boolean(detailedAnswer)}
        onReveal={revealSection}
        onAsk={onAskSuggestion}
      />

      <div className="mt-3 border border-warm-200 dark:border-gray-700">
        <button
          type="button"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={togglePanel}
          className="flex w-full items-center gap-2 bg-warm-100 px-3 py-2 text-left text-sm font-semibold text-primary transition-colors hover:bg-warm-200 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
        >
          <Chevron open={open} />
          <span>Ausführliche Analyse</span>
          <span className={`ml-auto ${badgeClass}`}>
            {reviewBadgeLabel(reviewAnnotation)}
          </span>
        </button>
        <div id={panelId}>
          {open && (
            <div className="px-3 py-3">
              <DetailPanelStatus
                status={detailStatus}
                statusMessage={detailStatusMessage}
                errorMessage={detailErrorMessage}
                onRetry={onRetryDetail || onExpandDetail}
              />
              {objections}
              {sections.map((section, index) => (
                <div
                  key={`section-${index}`}
                  {...(section.letter ? { id: sectionAnchorId(messageKey, section.letter) } : {})}
                >
                  {renderDetailedSection(section.text)}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnswerLayers;
